import { useCallback, useEffect } from 'react';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setDrawing, setContext, setSize, setInitialized, selectDrawing, selectContext, selectSize, selectInitialized } from '../store/slices/wallManagerSlice';
import { selectConfig, selectSelectedBricks } from '../store/slices/configurationSlice';
import { selectBricksLoading, selectBrickSeries } from '../store/slices/bricksSlice';
import { structures, Joint, BrickPattern, brickSizes } from '../constants';
import { getCachedImage } from '../utils/imageCache';

export const useWallManager = () => {
  const dispatch = useAppDispatch();
  const drawing = useAppSelector(selectDrawing);
  const ctx = useAppSelector(selectContext);
  const size = useAppSelector(selectSize);
  const initialized = useAppSelector(selectInitialized);
  const config = useAppSelector(selectConfig);
  const selectedBricks = useAppSelector(selectSelectedBricks);
  const bricksLoading = useAppSelector(selectBricksLoading);
  const brickSeries = useAppSelector(selectBrickSeries);

  const setCanvasContext = useCallback((context: CanvasRenderingContext2D | null) => {
    dispatch(setContext(context));
  }, [dispatch]);

  const setCanvasSize = useCallback((height: number, width: number) => {
    dispatch(setSize({ height, width }));
  }, [dispatch]);

  const getRandomBrick = useCallback((type: string) => {
    const bricks = Object.entries(selectedBricks).map(([k, rate]) => [
      k,
      rate,
      (brickSeries[k]?.bricks || []).filter((b) => getCachedImage(b.image)),
    ]);
    
    const r = Math.random() * 100;
    let edge = 0;
    for (let i = 0; i < bricks.length; i++) {
      edge += bricks[i][1] as number;
      if (r < edge) {
        const seriesBricks = bricks[i][2] as any[];
        return (
          _.sample(seriesBricks.filter((b) => b.type === type)) ??
          _.sample(seriesBricks)
        );
      }
    }
    const firstSeriesBricks = bricks[0]?.[2] as any[];
    return (
      _.sample(firstSeriesBricks?.filter((b) => b.type === type)) ??
      _.sample(firstSeriesBricks)
    );
  }, [selectedBricks, brickSeries]);

  const drawJointColor = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (!ctx) {
        resolve();
        return;
      }

      const joint = config.joint;

      if (joint) {
        const img = new Image();
        img.onload = () => {
          let drawnHeight = 0;
          let drawnWidth = 0;

          while (drawnHeight < size.height) {
            drawnWidth = 0;
            while (drawnWidth < size.width) {
              ctx.drawImage(img, drawnWidth, drawnHeight, img.width, img.height);
              drawnWidth += img.width;
            }
            drawnHeight += img.height;
          }
          resolve();
        };
        img.src = joint.img;
      } else {
        resolve();
      }
    });
  }, [ctx, config.joint, size.height, size.width]);

  const drawBricks = useCallback(() => {
    if (!ctx) return;
    
    const scale = config.scale;
    let totalDrawnHeight = config.jointSize[Joint.Horizontal] * 0.75 * scale;
    let totalDrawnWidth = 0;
    let rowNumber = 0;

    ctx.shadowBlur = 2 * scale;
    ctx.shadowOffsetY = 3 * scale;
    ctx.shadowOffsetX = 1 * scale;
    ctx.shadowColor = "rgba(0, 0, 0, 0.3)";

    const structure = structures[config.structure];

    if (!structure || config.bricks.length === 0) {
      return;
    }

    dispatch(setDrawing(true));

    while (totalDrawnHeight < size.height) {
      let height = 0;
      let width = 0;

      rowNumber = rowNumber % structure.rows.length;
      totalDrawnWidth = 0;
      let brickNumber = 0;

      while (totalDrawnWidth < size.width) {
        let type = structure.rows[rowNumber].pattern[brickNumber % structure.rows[rowNumber].pattern.length];
        let joint = (type === BrickPattern.Half ? 0.5 : 1) * scale;

        const brick = getRandomBrick(type);
        const img = brick ? getCachedImage(brick.image) : null;

        if (!img) break;

        height = brickSizes[config.brickSize].height * scale;
        width = brickSizes[config.brickSize].width * (type === BrickPattern.Half ? 0.5 : 1) * scale;

        let jointWidth = config.jointSize[Joint.Vertical] * scale;
        let squeeze = jointWidth - jointWidth * joint;
        if (squeeze > 0) {
          width -= squeeze;
        }

        let margin = 0;
        const currentRow = structure.rows[rowNumber];
        if (totalDrawnWidth === 0 && currentRow && currentRow.margin !== undefined) {
          margin = width * currentRow.margin;
          margin += config.jointSize[Joint.Vertical] * scale * currentRow.margin;
        }

        drawImageOnCanvas(img, totalDrawnWidth + margin, totalDrawnHeight, width, height);
        totalDrawnWidth += width + margin;
        totalDrawnWidth += jointWidth;
        brickNumber++;
      }

      totalDrawnHeight += height;
      totalDrawnHeight += config.jointSize[Joint.Horizontal] * scale;
      rowNumber++;
    }
    
    dispatch(setDrawing(false));
  }, [ctx, config, size, getRandomBrick, dispatch]);

  const drawImageOnCanvas = useCallback((img: HTMLImageElement, x: number, y: number, width: number, height: number) => {
    if (!ctx) return;
    
    const rand = Math.floor(Math.random());
    ctx.save();
    if (rand === 1) {
      ctx.scale(-1, 1);
      ctx.drawImage(img, -1 * x - width, y, width, height);
    } else {
      ctx.drawImage(img, x, y, width, height);
    }
    ctx.restore();
  }, [ctx]);

  const draw = useCallback(() => {
    if (!ctx) return;

    ctx.clearRect(0, 0, size.width, size.height);
    drawJointColor().then(() => drawBricks());
  }, [ctx, size.width, size.height, drawJointColor, drawBricks]);

  const startDrawing = useCallback(() => {
    if (!initialized) {
      dispatch(setInitialized(true));
    }
  }, [initialized, dispatch]);

  // Auto-draw effect
  useEffect(() => {
    if (initialized && config && !bricksLoading && Object.keys(selectedBricks).length > 0) {
      draw();
    }
  }, [initialized, config, bricksLoading, selectedBricks, draw]);

  return {
    drawing,
    setContext: setCanvasContext,
    setSize: setCanvasSize,
    draw,
    startDrawing,
    getRandomBrick,
  };
};